/**
 * VendorEarningsDashboard — EntrepreNatives-branded Wallet view.
 * Now tabbed: Cash · Bank Account · Settings. The Wallet shell lives here
 * (per user direction Feb 2026) — the per-vendor money home.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DollarSign, Clock, TrendingUp, ArrowRight, Loader2, AlertCircle, Check,
  Calendar, Banknote, Zap, Wallet, Building2, Settings as SettingsIcon, Lock, Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectComponentsProvider, ConnectPayouts, ConnectBalances, ConnectAccountManagement, ConnectNotificationBanner } from '@stripe/react-connect-js';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import {
  GlassCard, StatCardV2, DashboardBackdrop, GradientAreaChart, GradientIconTile,
} from '@/components/dashboard/dashboard-ui';
import InstantPayoutModal from '@/components/vendor/InstantPayoutModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

const fmt = (n, ccy) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (ccy || 'USD').toUpperCase(),
    maximumFractionDigits: 2,
  }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

const STATUS_DOT = {
  paid:       '#A3E635',
  in_transit: '#22D3EE',
  pending:    '#F59E0B',
  failed:     '#FB7185',
  canceled:   '#A5A8C9',
};

function buildMonthlySeries(history = []) {
  if (!history.length) {
    const today = new Date();
    return Array.from({ length: 6 }).map((_, i) => {
      const d = new Date(today.getFullYear(), today.getMonth() - 5 + i, 1);
      return { name: d.toLocaleDateString('en-US', { month: 'short' }), earnings: 0 };
    });
  }
  const buckets = {};
  history.forEach((p) => {
    const d = p.arrived_on ? new Date(p.arrived_on) : new Date();
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    buckets[key] = (buckets[key] || 0) + Math.abs(Number(p.amount) || 0);
  });
  return Object.keys(buckets)
    .sort()
    .slice(-6)
    .map((k) => {
      const [y, m] = k.split('-');
      const d = new Date(Number(y), Number(m) - 1, 1);
      return {
        name: d.toLocaleDateString('en-US', { month: 'short' }),
        earnings: Math.round(buckets[k] * 100) / 100,
      };
    });
}

export default function VendorEarningsDashboard() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [tab, setTab] = useState('cash');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [err, setErr] = useState('');
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [instantModalOpen, setInstantModalOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [statusRes, earningsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/vendor/payout-wizard/status`, { headers: getAuthHeaders() }),
        axios.get(`${API}/vendor/payout-wizard/earnings`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/vendor/payout-wizard/payout-settings`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
      ]);
      setStatus(statusRes.data);
      setData(earningsRes.data);
      setSettings(settingsRes.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load earnings');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const series = useMemo(
    () => buildMonthlySeries(data?.payout_history || []),
    [data?.payout_history]
  );

  const handlePayout = async (method) => {
    // Instant payouts now use a proper dark-glass modal with amount input,
    // live fee preview and animated receipt — replacing the old window.confirm().
    if (method === 'instant') {
      setInstantModalOpen(true);
      return;
    }
    const amount = data?.available_balance || 0;
    if (amount <= 0) {
      toast.error('No funds available to cash out yet');
      return;
    }
    const confirmMsg = `Send standard payout of ${fmt(amount, data?.currency)} to your bank? Free — arrives in 2–3 business days.`;
    if (!window.confirm(confirmMsg)) return;
    setPayoutBusy(true);
    try {
      const r = await axios.post(`${API}/vendor/payout-wizard/payout`, { method }, { headers: getAuthHeaders() });
      toast.success('Standard payout queued — arrives in 2–3 business days');
      await load();
      console.log('Payout receipt:', r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payout failed');
    } finally {
      setPayoutBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#06061A] text-white">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-20 text-center text-white/60">
          <Loader2 className="animate-spin mx-auto" size={24} />
          <p className="mt-3 text-sm">Loading your earnings…</p>
        </div>
      </div>
    );
  }

  const needsSetup = !status?.payout_ready;
  const ccy = data?.currency || 'usd';

  const TABS = [
    { id: 'cash',      label: 'Cash',             icon: Wallet },
    { id: 'transfers', label: 'Order Payouts',    icon: ArrowRight },
    { id: 'bank',      label: 'Bank Account',     icon: Building2 },
    { id: 'settings',  label: 'Settings',         icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-[#06061A] text-white">
      <Navbar />
      <main className="relative max-w-6xl mx-auto px-6 py-12" data-testid="earnings-dashboard">
        <DashboardBackdrop />
        <div className="relative z-10 space-y-8">
          {/* Header */}
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-violet-300 font-bold">EntrepreNatives</p>
              <h1 className="text-3xl md:text-4xl font-bold mt-2 font-syne tracking-tight">Wallet</h1>
              <p className="text-white/55 mt-2 text-sm">
                Your money on EntrepreNatives — balances, payouts, bank account, and settings.
              </p>
            </div>
            {!needsSetup ? (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/12 border border-emerald-400/30 text-emerald-200 text-xs font-bold tracking-wider uppercase" data-testid="payouts-active-badge">
                <Check size={13} /> Payouts active
              </div>
            ) : (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/12 border border-amber-400/30 text-amber-200 text-xs font-bold tracking-wider uppercase">
                <Clock size={13} /> Setup pending
              </div>
            )}
          </div>

          {err && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-400/30 text-red-200 text-sm">
              <AlertCircle size={16} /> {err}
            </div>
          )}

          {needsSetup && (
            <div className="relative overflow-hidden bg-gradient-to-br from-violet-600/20 to-fuchsia-600/15 border border-violet-400/30 rounded-2xl p-6 flex items-center justify-between gap-4" data-testid="earnings-needs-setup">
              <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-violet-500/20 blur-3xl" />
              <div className="relative">
                <p className="text-xs uppercase tracking-wider font-bold text-violet-200">Almost there</p>
                <h2 className="text-xl font-bold mt-1 font-syne">
                  {status?.step1_completed ? 'Finish setting up your payouts' : 'Set up your payouts to start selling'}
                </h2>
                <p className="text-sm text-white/70 mt-2 max-w-xl">
                  We need a few details so we can pay you when customers buy. Takes about 3 minutes.
                </p>
              </div>
              <button onClick={() => navigate('/vendor/setup-payouts')} className="relative flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-[#0E0E23] font-bold text-sm uppercase tracking-wider hover:bg-violet-100" data-testid="earnings-cta-setup-payouts">
                Continue setup <ArrowRight size={14} />
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="wallet-tabs">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-[0.16em] border transition-all ${
                    active
                      ? 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] border-transparent text-white shadow-[0_8px_24px_rgba(139,92,246,0.35)]'
                      : 'bg-white/[0.04] border-white/[0.08] text-white/65 hover:bg-white/[0.08] hover:text-white'
                  }`}
                  data-testid={`wallet-tab-${t.id}`}
                >
                  <t.icon size={13} /> {t.label}
                </button>
              );
            })}
          </div>

          {/* ─── CASH TAB ─── */}
          {tab === 'cash' && (
            <div className="space-y-8" data-testid="wallet-cash-pane">
              {/* 4 Stat Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <StatCardV2
                  icon={DollarSign} label="Available Balance"
                  value={data?.available_balance || 0}
                  format={(v) => fmt(v, ccy)} ramp="lime"
                  spark={[2, 4, 3, 7, 5, 9, 8, 12].map((y, x) => ({ x, y }))}
                  sparkId="avail" data-testid="stat-available-balance" delay={0}
                />
                <StatCardV2
                  icon={Clock} label="Pending Funds"
                  value={data?.pending_balance || 0}
                  format={(v) => fmt(v, ccy)} ramp="amber"
                  spark={[1, 3, 2, 5, 4, 7, 6, 9].map((y, x) => ({ x, y }))}
                  sparkId="pend" data-testid="stat-pending-balance" delay={0.08}
                />
                <StatCardV2
                  icon={TrendingUp} label="Lifetime Earnings"
                  value={data?.lifetime_earnings || 0}
                  format={(v) => fmt(v, ccy)} ramp="violet"
                  spark={[3, 5, 4, 8, 6, 11, 9, 14].map((y, x) => ({ x, y }))}
                  sparkId="life" data-testid="stat-lifetime-earnings" delay={0.16}
                />
                <StatCardV2
                  icon={Calendar} label="Next Payout"
                  value={data?.next_payout_date ? 1 : 0}
                  format={() => (data?.next_payout_date ? fmtDate(data.next_payout_date) : '—')}
                  ramp="cyan"
                  spark={[1, 2, 2, 4, 3, 5, 4, 7].map((y, x) => ({ x, y }))}
                  sparkId="nextp" data-testid="stat-next-payout" delay={0.24}
                />
              </div>

              {/* Cash-out buttons */}
              {!needsSetup && (
                <GlassCard glow className="p-6" data-testid="cashout-panel">
                  <div className="flex items-center gap-3 mb-4">
                    <GradientIconTile icon={Zap} ramp="lime" size="sm" />
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-0.5">Cash out</p>
                      <h3 className="text-lg font-bold text-white font-syne">Get your money — your way</h3>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <button
                      onClick={() => handlePayout('instant')}
                      disabled={payoutBusy}
                      className={`text-left rounded-xl p-5 border transition-all disabled:opacity-40 disabled:cursor-not-allowed group ${
                        settings?.instant_eligible
                          ? 'bg-gradient-to-br from-lime-500/20 to-emerald-600/15 border-lime-400/30 hover:border-lime-300 hover:shadow-[0_0_24px_rgba(163,230,53,0.25)]'
                          : 'bg-white/[0.03] border-white/[0.08] hover:border-amber-400/40'
                      }`}
                      data-testid="cashout-instant"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold ${settings?.instant_eligible ? 'text-lime-300' : 'text-amber-300'}`}>
                          <Zap size={11} className={settings?.instant_eligible ? 'fill-lime-300' : ''} />
                          {settings?.instant_eligible ? 'Instant · 1.5% fee' : 'Instant · Setup required'}
                        </span>
                        <ArrowRight size={14} className={`group-hover:translate-x-0.5 transition-transform ${settings?.instant_eligible ? 'text-lime-300' : 'text-amber-300'}`} />
                      </div>
                      <p className="text-2xl font-bold text-white font-syne">{settings?.instant_eligible ? 'Cash out now' : 'Unlock instant payouts'}</p>
                      <p className="text-xs text-white/55 mt-1">
                        {settings?.instant_eligible
                          ? 'Money in your bank in minutes. Stripe deducts the 1.5% fee from your payout.'
                          : 'Add a debit card in the Bank tab to enable Instant Payouts (Stripe charges 1.5%).'}
                      </p>
                    </button>
                    <button
                      onClick={() => handlePayout('standard')}
                      disabled={payoutBusy || (data?.available_balance || 0) <= 0}
                      className="text-left rounded-xl p-5 bg-white/[0.04] border border-white/[0.08] hover:border-white/30 hover:bg-white/[0.07] transition-all disabled:opacity-40 disabled:cursor-not-allowed group"
                      data-testid="cashout-standard"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#A5A8C9] font-bold">
                          <Calendar size={11} /> Standard · Free
                        </span>
                        <ArrowRight size={14} className="text-white/55 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                      <p className="text-2xl font-bold text-white font-syne">Standard payout</p>
                      <p className="text-xs text-white/55 mt-1">Free — arrives in your bank in 2–3 business days.</p>
                    </button>
                  </div>
                  <p className="text-[11px] text-[#7B7FA8] mt-3 leading-relaxed">
                    Cash-outs go directly to the bank account on file. Payment processing is handled by Stripe — EntrepreNatives does not control payout timing, hold your money, or store your bank digits.
                  </p>
                </GlassCard>
              )}

              {/* Earnings chart */}
              <GlassCard glow className="p-6" data-testid="earnings-chart-card">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Trend</p>
                    <h3 className="text-lg font-bold font-syne">Earnings Over Time</h3>
                    <p className="text-xs text-[#A5A8C9] mt-0.5">Monthly payouts from EntrepreNatives</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-[#A5A8C9]">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#A3E635', boxShadow: '0 0 10px rgba(163,230,53,0.7)' }} />
                    Earnings
                  </div>
                </div>
                <GradientAreaChart
                  data={series}
                  height={260}
                  series={[{ key: 'earnings', label: 'Earnings', ramp: 'lime' }]}
                  yFormatter={(v) => fmt(v, ccy)}
                />
              </GlassCard>

              {/* Payout history */}
              <GlassCard glow className="overflow-hidden" data-testid="payout-history-card">
                <div className="flex items-center justify-between p-6 pb-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">History</p>
                    <h3 className="text-lg font-bold font-syne">Payouts to your bank</h3>
                  </div>
                  <Banknote size={18} className="text-[#A78BFA]" />
                </div>
                {(!data?.payout_history || data.payout_history.length === 0) ? (
                  <p className="text-sm text-white/45 py-6 px-6 text-center">No payouts yet — sales will appear here once they clear.</p>
                ) : (
                  <div className="px-2 pb-4">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-[0.16em] text-[#7B7FA8] font-semibold">
                          <th className="px-4 pb-3 pt-1">Description</th>
                          <th className="px-4 pb-3 pt-1">Status</th>
                          <th className="px-4 pb-3 pt-1">Arrived</th>
                          <th className="px-4 pb-3 pt-1 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.payout_history.map((p) => {
                          const dot = STATUS_DOT[p.status] || STATUS_DOT.pending;
                          return (
                            <tr key={p.id} className="border-t border-white/[0.04] hover:bg-white/[0.025]" data-testid={`payout-row-${p.id}`}>
                              <td className="px-4 py-3.5 font-semibold text-white">{p.description}</td>
                              <td className="px-4 py-3.5">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold capitalize" style={{ color: dot, background: `${dot}1A`, border: `1px solid ${dot}40` }}>
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot, boxShadow: `0 0 8px ${dot}` }} />
                                  {String(p.status || 'pending').replace('_', ' ')}
                                </span>
                              </td>
                              <td className="px-4 py-3.5 text-[#A5A8C9] text-xs">{fmtDate(p.arrived_on)}</td>
                              <td className="px-4 py-3.5 text-right font-bold text-white tabular-nums">{fmt(p.amount, p.currency || ccy)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </GlassCard>
            </div>
          )}

          {/* ─── BANK TAB ─── */}
          {tab === 'bank' && (
            <BankAccountPane
              status={status}
              settings={settings}
              getAuthHeaders={getAuthHeaders}
              navigate={navigate}
              onSetupClick={() => navigate('/vendor/setup-payouts')}
              onChange={load}
            />
          )}

          {/* ─── ORDER PAYOUTS TAB ─── */}
          {tab === 'transfers' && (
            <TransfersPane getAuthHeaders={getAuthHeaders} />
          )}

          {/* ─── SETTINGS TAB ─── */}
          {tab === 'settings' && (
            <SettingsPane
              status={status}
              settings={settings}
            />
          )}
        </div>
      </main>
      <InstantPayoutModal
        open={instantModalOpen}
        onClose={() => setInstantModalOpen(false)}
        available={data?.available_balance || 0}
        currency={data?.currency || 'usd'}
        settings={settings}
        getAuthHeaders={getAuthHeaders}
        onPayoutComplete={() => { load(); }}
        onOpenBank={() => setTab('bank')}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 *  Bank Account pane — embedded Stripe ConnectPayouts component
 * ────────────────────────────────────────────────────────────────────── */
function BankAccountPane({ status, settings, getAuthHeaders, onSetupClick }) {
  const [connect, setConnect] = useState(null);
  const [initErr, setInitErr] = useState('');

  useEffect(() => {
    if (!status?.payout_ready || !STRIPE_PK) return;
    let cancelled = false;

    const fetchSecret = async () => {
      try {
        // Pull from /account-session — the backend now enables payouts +
        // balances + account_management + notification_banner on this one
        // session so every embedded component below can share it.
        const r = await axios.post(`${API}/vendor/payout-wizard/account-session`, {}, { headers: getAuthHeaders() });
        return r.data?.client_secret;
      } catch (e) {
        setInitErr(e.response?.data?.detail || 'Could not open Stripe bank session');
        return null;
      }
    };

    const instance = loadConnectAndInitialize({
      publishableKey: STRIPE_PK,
      fetchClientSecret: fetchSecret,
      appearance: {
        overlays: 'dialog',
        variables: {
          colorPrimary: '#8B5CF6',
          colorBackground: '#0E0E23',
          colorText: '#FFFFFF',
          colorSecondaryText: '#A5A8C9',
          colorBorder: 'rgba(255,255,255,0.08)',
          borderRadius: '12px',
          fontFamily: 'Syne, system-ui, sans-serif',
        },
      },
    });
    if (!cancelled) setConnect(instance);
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.payout_ready]);

  if (!status?.payout_ready) {
    return (
      <GlassCard glow className="p-8 text-center" data-testid="bank-pane-setup">
        <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)', boxShadow: '0 10px 30px -8px rgba(139,92,246,0.5)' }}>
          <Building2 size={26} className="text-white" />
        </div>
        <h3 className="text-xl font-bold font-syne">Finish payout setup first</h3>
        <p className="text-sm text-white/55 mt-2 max-w-md mx-auto">
          Once your business and contact info are in, you'll be able to add a bank account here.
        </p>
        <button onClick={onSetupClick} className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-xl bg-white text-[#0E0E23] font-bold text-sm uppercase tracking-wider hover:bg-violet-100">
          Continue setup <ArrowRight size={14} />
        </button>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-5" data-testid="bank-pane">
      {/* Privacy disclosure */}
      <GlassCard className="p-5 border-violet-500/20" data-testid="bank-privacy-card">
        <div className="flex items-start gap-3">
          <GradientIconTile icon={Shield} ramp="violet" size="sm" />
          <div className="flex-1">
            <p className="text-white font-bold text-sm font-syne">Your bank details stay private</p>
            <p className="text-xs text-white/65 mt-1 leading-relaxed">
              EntrepreNatives never sees, stores, or controls your bank account information. Your bank digits live exclusively
              inside Stripe's secure wallet — the same infrastructure that powers Shopify, Lyft, and DoorDash payouts.
              We can't view, edit, or share them.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] text-white/45">
              <span className="inline-flex items-center gap-1"><Lock size={10} /> PCI-DSS Level 1</span>
              <span>·</span>
              <span>Bank-grade encryption (Stripe)</span>
              <span>·</span>
              <span>Master admins cannot view</span>
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Instant Payouts upsell — shown when a bank is on file but no debit card */}
      {settings?.external_account_last4 && settings?.external_account_type === 'bank_account' && !settings?.instant_eligible && (
        <GlassCard glow className="p-5 border-lime-400/30" data-testid="bank-add-debit-card-cta">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-[260px]">
              <GradientIconTile icon={Zap} ramp="lime" size="sm" />
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.18em] text-lime-300 font-bold mb-0.5">Unlock Instant Payouts</p>
                <p className="text-white font-bold text-sm font-syne">Add a debit card to cash out in minutes</p>
                <p className="text-xs text-white/65 mt-1 leading-relaxed">
                  You currently have a bank account on file (only supports 2–3 day Standard payouts). Adding a debit card below
                  unlocks Instant Payouts — money in your bank in minutes for a 1.5% Stripe fee.
                </p>
                <p className="text-[10px] text-white/40 mt-1">
                  Your card details are entered directly inside Stripe's secure box below. EntrepreNatives never sees the digits.
                </p>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  const r = await axios.post(
                    `${API}/stripe/connect/update-link`,
                    { origin_url: window.location.origin },
                    { headers: getAuthHeaders() }
                  );
                  if (r.data?.url) {
                    window.open(r.data.url, '_blank', 'noopener');
                  } else {
                    toast.error('Could not open Stripe — please try again.');
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Could not open Stripe — please try again.');
                  // Fall back to in-page scroll to embedded Stripe frame.
                  const el = document.querySelector('[data-testid="bank-stripe-frame"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-lime-500/20 to-emerald-600/20 border border-lime-400/40 text-lime-200 text-xs font-bold uppercase tracking-wider hover:bg-lime-500/25 hover:border-lime-300 transition-all"
              data-testid="bank-scroll-to-stripe-btn"
            >
              Add debit card <ArrowRight size={13} />
            </button>
          </div>
        </GlassCard>
      )}

      {/* Current bank account preview (last 4 only) */}
      {settings?.external_account_last4 && (
        <GlassCard glow className="p-5" data-testid="bank-current-card">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <GradientIconTile icon={Banknote} ramp="lime" size="sm" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-0.5">On file</p>
                <p className="text-white font-bold text-sm">
                  {settings.external_account_bank || 'Bank account'} <span className="text-white/55 font-mono">••••{settings.external_account_last4}</span>
                </p>
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Active</span>
          </div>
        </GlassCard>
      )}

      {/* Embedded Stripe Connect payouts UI */}
      <GlassCard glow className="p-2" data-testid="bank-stripe-frame">
        {initErr && (
          <div className="m-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-400/30 text-red-200 text-sm">
            <AlertCircle size={16} /> {initErr}
          </div>
        )}
        {!STRIPE_PK && (
          <div className="m-4 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-400/30 text-amber-200 text-xs">
            Add `REACT_APP_STRIPE_PK` to your frontend env to enable inline bank account management.
          </div>
        )}
        {connect && (
          <ConnectComponentsProvider connectInstance={connect}>
            {/* Shopify-style stack: action banner → live balance → bank /
                payout controls → self-serve account mgmt. Every component
                shares the same AccountSession so there's only one Stripe
                round-trip per page. */}
            <div className="px-2 pt-2" data-testid="bank-notification-banner">
              <ConnectNotificationBanner />
            </div>
            <div className="mt-3 rounded-xl overflow-hidden" data-testid="bank-balances">
              <ConnectBalances />
            </div>
            <div className="mt-3 rounded-xl overflow-hidden" data-testid="bank-payouts">
              <ConnectPayouts />
            </div>
            <div className="mt-3 rounded-xl overflow-hidden" data-testid="bank-account-management">
              <ConnectAccountManagement />
            </div>
          </ConnectComponentsProvider>
        )}
      </GlassCard>

      <p className="text-[10px] text-[#7B7FA8] leading-relaxed px-1">
        EntrepreNatives is a marketplace facilitator. Payouts are handled by Stripe (a licensed payment processor) and
        funds move directly from the buyer's payment to the bank account you connect above — EntrepreNatives never
        holds your money. Stripe's standard fee schedule and payout timing apply.
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 *  Settings pane — currency display + auto-conversion explanation
 * ────────────────────────────────────────────────────────────────────── */
function SettingsPane({ status, settings }) {
  const ccy = (settings?.currency || status?.currency || 'usd').toUpperCase();
  const country = settings?.country || status?.country || 'US';
  const ccyMeta = ccy === 'CAD'
    ? { label: 'Canadian Dollar', flag: '🇨🇦', desc: 'You receive payouts in CAD.' }
    : { label: 'US Dollar', flag: '🇺🇸', desc: 'You receive payouts in USD.' };

  return (
    <div className="space-y-5" data-testid="settings-pane">
      {/* Payout currency */}
      <GlassCard glow className="p-6" data-testid="settings-currency-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <GradientIconTile icon={DollarSign} ramp="violet" size="lg" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Payout currency</p>
              <p className="text-2xl font-bold text-white font-syne flex items-center gap-2">
                <span>{ccyMeta.flag}</span> {ccy} <span className="text-sm font-medium text-white/55">{ccyMeta.label}</span>
              </p>
              <p className="text-xs text-white/55 mt-1">{ccyMeta.desc}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold mb-1">Account country</p>
            <p className="text-white font-bold">{country}</p>
          </div>
        </div>
      </GlassCard>

      {/* Auto-conversion explanation */}
      <GlassCard glow className="p-6" data-testid="settings-autoconvert-card">
        <div className="flex items-start gap-3">
          <GradientIconTile icon={Zap} ramp="cyan" size="sm" />
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Auto-conversion</p>
            <h3 className="text-base font-bold text-white font-syne">Buyers pay in their currency — we convert to yours</h3>
            <p className="text-sm text-white/65 mt-2 leading-relaxed">
              When someone buys from you, they pay in whatever currency their card uses (USD or CAD). Stripe automatically
              converts that to <span className="font-bold text-white">{ccy}</span> at the daily market exchange rate before
              it lands in your wallet. You don't have to think about FX — it just works.
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <div className="p-3 rounded-xl bg-white/[0.04] border border-white/[0.08]">
                <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Buyer in 🇺🇸</p>
                <p className="text-sm text-white mt-1">Pays <span className="font-bold">$100 USD</span></p>
                <p className="text-xs text-white/55 mt-0.5">→ you receive {ccy === 'CAD' ? '~$137 CAD' : '$100 USD'}</p>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.04] border border-white/[0.08]">
                <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Buyer in 🇨🇦</p>
                <p className="text-sm text-white mt-1">Pays <span className="font-bold">$100 CAD</span></p>
                <p className="text-xs text-white/55 mt-0.5">→ you receive {ccy === 'CAD' ? '$100 CAD' : '~$73 USD'}</p>
              </div>
            </div>
            <p className="text-[10px] text-[#7B7FA8] mt-3">
              Stripe applies a small FX margin (typically ~2%) on cross-currency transactions, deducted automatically.
            </p>
          </div>
        </div>
      </GlassCard>

      {/* Instant eligibility status */}
      <GlassCard className="p-5" data-testid="settings-instant-card">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <GradientIconTile icon={Zap} ramp={settings?.instant_eligible ? 'lime' : 'amber'} size="sm" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-0.5">Instant payouts</p>
              <p className="text-white font-bold text-sm">
                {settings?.instant_eligible ? 'Eligible — debit card on file' : 'Not eligible yet'}
              </p>
              <p className="text-[11px] text-white/55 mt-0.5">
                {settings?.instant_eligible
                  ? 'You can cash out instantly for a 1.5% Stripe fee.'
                  : 'Add a debit card to your Stripe wallet to enable Instant Payouts. Bank accounts only support standard 2–3 day payouts.'}
              </p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${settings?.instant_eligible ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-400/30' : 'bg-amber-500/15 text-amber-200 border border-amber-400/30'}`}>
            {settings?.instant_eligible ? 'On' : 'Off'}
          </span>
        </div>
      </GlassCard>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// TransfersPane — per-order auto-payout history
// ──────────────────────────────────────────────────────────────────────────
function TransfersPane({ getAuthHeaders }) {
  const [items,    setItems]    = useState([]);
  const [summary,  setSummary]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.allSettled([
        axios.get(`${API}/vendor/payouts/transfers`, { headers: getAuthHeaders() }),
        axios.get(`${API}/vendor/payouts/summary`,   { headers: getAuthHeaders() }),
      ]);
      if (a.status === 'fulfilled') setItems(a.value.data?.items || []);
      if (b.status === 'fulfilled') setSummary(b.value.data);
    } catch { /* swallow */ }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const retry = async (orderId) => {
    setRetrying(orderId);
    try {
      const r = await axios.post(`${API}/vendor/payouts/retry/${orderId}`, {}, { headers: getAuthHeaders() });
      if (r.data?.status === 'paid') toast.success('Transfer succeeded — net amount sent to your Stripe account');
      else toast.error(r.data?.error || 'Still cannot transfer — check your Stripe Connect status');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
    finally { setRetrying(null); }
  };

  const STATUS_PILL = {
    paid:            { cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/25', label: 'Paid' },
    pending_connect: { cls: 'bg-amber-500/15 text-amber-300 border-amber-400/25',         label: 'Held — connect Stripe' },
    failed:          { cls: 'bg-red-500/15 text-red-300 border-red-400/25',                label: 'Failed' },
  };

  const totals = summary?.by_status || {};
  const paid    = totals.paid    || { count: 0, gross: 0, fees: 0, net: 0 };
  const pending = totals.pending_connect || { count: 0, gross: 0, fees: 0, net: 0 };
  const failed  = totals.failed  || { count: 0, gross: 0, fees: 0, net: 0 };

  return (
    <div className="space-y-6" data-testid="transfers-pane">
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-2">Order Payouts</p>
        <h2 className="text-2xl font-bold text-white font-syne">Automatic transfers</h2>
        <p className="text-[#A5A8C9] text-sm mt-1.5">
          Every time a customer pays for an order, we automatically transfer your share — gross minus the {summary?.platform_fee_pct ?? 5}% platform fee — into your connected Stripe account. From there, Stripe sweeps to your bank.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCardV2 label="Paid out" value={fmt(paid.net)} sublabel={`${paid.count} orders`} ramp="emerald" icon={Check} />
        <StatCardV2 label="Held — connect Stripe" value={fmt(pending.net)} sublabel={`${pending.count} pending`} ramp="amber" icon={Clock} />
        <StatCardV2 label="Failed" value={fmt(failed.net)} sublabel={`${failed.count} retry-able`} ramp="rose" icon={AlertCircle} />
      </div>

      <GlassCard className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center gap-2 text-white/55">
            <Loader2 size={14} className="animate-spin" /> Loading transfers…
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <ArrowRight size={28} className="text-white/30 mx-auto mb-3" />
            <p className="text-white/65 font-semibold">No order payouts yet</p>
            <p className="text-[12.5px] text-white/45 mt-1.5 max-w-md mx-auto">
              When a customer pays for one of your services or products, the transfer will land here automatically — no clicks required.
            </p>
          </div>
        ) : (
          <table className="w-full" data-testid="transfers-table">
            <thead className="bg-white/[0.02] border-b border-white/[0.06]">
              <tr>
                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-white/55">Order</th>
                <th className="text-right px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-white/55">Gross</th>
                <th className="text-right px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-white/55">Fee</th>
                <th className="text-right px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-white/55">Net</th>
                <th className="text-left  px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-white/55">Status</th>
                <th className="text-left  px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-white/55">Date</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const meta = STATUS_PILL[row.status] || STATUS_PILL.failed;
                const canRetry = row.status === 'pending_connect' || row.status === 'failed';
                return (
                  <tr key={row.id || row.order_id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`transfer-row-${row.order_id}`}>
                    <td className="px-5 py-3 text-[12.5px] text-white/85 font-mono truncate" title={row.order_id}>
                      {(row.order_id || '').slice(0, 12)}…
                    </td>
                    <td className="px-5 py-3 text-right text-[12.5px] text-white/65">{fmt(row.gross_amount, row.currency)}</td>
                    <td className="px-5 py-3 text-right text-[12.5px] text-white/45">{fmt(row.platform_fee, row.currency)}</td>
                    <td className="px-5 py-3 text-right text-[13px] text-white font-semibold">{fmt(row.vendor_net, row.currency)}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[9.5px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
                      {row.error && <p className="text-[10.5px] text-red-300/70 mt-1 truncate max-w-[220px]" title={row.error}>{row.error}</p>}
                    </td>
                    <td className="px-5 py-3 text-[11.5px] text-white/55">{fmtDate(row.created_at)}</td>
                    <td className="px-5 py-3 text-right">
                      {canRetry && (
                        <button
                          onClick={() => retry(row.order_id)}
                          disabled={retrying === row.order_id}
                          className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] hover:shadow-[0_8px_22px_rgba(139,92,246,0.4)] rounded-md flex items-center gap-1.5 disabled:opacity-50 ml-auto"
                          data-testid={`transfer-retry-${row.order_id}`}
                        >
                          {retrying === row.order_id ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </GlassCard>

      <GlassCard className="p-5 bg-violet-500/[0.04] border-violet-400/15">
        <p className="text-[11px] uppercase tracking-[0.18em] text-violet-300 font-bold mb-2">How this works</p>
        <ul className="space-y-2 text-[12.5px] text-white/75">
          <li>• <strong>Stripe is the default</strong> — connect once, get paid automatically. Stripe handles tax forms, currency conversion, and bank routing.</li>
          <li>• <strong>Don&apos;t want Stripe?</strong> Switch your default to E-Transfer or PayPal in <em>Settings</em>. The platform marks orders as &quot;Held&quot; and master sends manually within 5 business days (eligible vendors only).</li>
          <li>• <strong>Held → Paid:</strong> if you connect Stripe later, click <em>Retry</em> on any held row and the transfer fires immediately.</li>
        </ul>
      </GlassCard>
    </div>
  );
}
